import { useEffect, useState } from "react";
import { useGetIdentity } from "ra-core";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import {
  Phone,
  TrendingUp,
  Calendar,
  Users,
  Target,
  ArrowUpRight,
  ArrowUp,
  ArrowDown,
  Clock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

import { SERA_PHONE } from "@/components/atomic-crm/misc/constants";

const supabase = getSupabaseClient();

type ConversationRow = {
  id: string;
  caller_name: string | null;
  phone_number: string | null;
  appointment_booked: boolean | null;
  duration_seconds: number | null;
  created_at: string;
};

type ContactRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  outreach_status: string | null;
};

const OUTREACH_STAGES = [
  { id: "not_contacted", label: "Not Contacted", color: "#CBD5E1" },
  { id: "dm_sent", label: "DM Sent", color: "#93C5FD" },
  { id: "replied", label: "Replied", color: "#FCD34D" },
  { id: "call_scheduled", label: "Call Scheduled", color: "#C4B5FD" },
  { id: "demo_done", label: "Demo Done", color: "#86EFAC" },
  { id: "proposal_sent", label: "Proposal Sent", color: "#67E8F9" },
  { id: "negotiating", label: "Negotiating", color: "#FCA5A5" },
  { id: "closed_won", label: "Closed Won", color: "#3ECF8E" },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-3.5 py-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: p.fill || p.stroke }}
          />
          <span className="text-slate-500 capitalize">
            {p.name || p.dataKey}
          </span>
          <span className="font-bold text-slate-900 ml-auto pl-4">
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const KPICard = ({
  label,
  value,
  sub,
  icon: Icon,
  trend,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: number;
  accent?: boolean;
}) => (
  <div
    className={
      accent
        ? "rounded-2xl border p-6"
        : "bg-white rounded-2xl border border-[#E8ECF0] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-6"
    }
    style={
      accent
        ? {
            background: "rgba(62,207,142,0.08)",
            borderColor: "rgba(62,207,142,0.2)",
          }
        : {}
    }
  >
    <div className="flex items-center justify-between mb-4">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: accent ? "rgba(26,140,91,0.65)" : "#94A3B8" }}
      >
        {label}
      </p>
      <Icon className="w-3.5 h-3.5 text-slate-300" />
    </div>
    <p
      className="text-[40px] font-bold tracking-tight tabular-nums leading-none mb-2"
      style={{ color: accent ? "#3ECF8E" : "#0F1629" }}
    >
      {value}
    </p>
    <div className="flex items-center gap-1.5">
      {trend !== undefined && trend !== 0 && (
        <>
          {trend > 0 ? (
            <ArrowUp className="w-3 h-3" style={{ color: "#3ECF8E" }} />
          ) : (
            <ArrowDown className="w-3 h-3 text-red-400" />
          )}
          <span
            className="text-[12px] font-semibold"
            style={{ color: trend > 0 ? "#3ECF8E" : "#F87171" }}
          >
            {Math.abs(trend)}%
          </span>
        </>
      )}
      {sub && (
        <span
          className="text-[12px]"
          style={{ color: accent ? "rgba(26,140,91,0.6)" : "#94A3B8" }}
        >
          {sub}
        </span>
      )}
    </div>
  </div>
);

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

export const OwnerDashboard = () => {
  const { identity } = useGetIdentity();
  const displayName =
    identity?.fullName ?? identity?.email?.split("@")[0] ?? "there";
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const [{ data: convData }, { data: contactData }] = await Promise.all([
        supabase
          .from("conversations")
          .select(
            "id,caller_name,phone_number,appointment_booked,duration_seconds,created_at",
          )
          .order("created_at", { ascending: false })
          .limit(200),
        supabase
          .from("contacts")
          .select("id,first_name,last_name,outreach_status")
          .limit(2000),
      ]);
      setConversations(convData || []);
      setContacts(contactData || []);
      setLoading(false);
    };
    fetch();
  }, []);

  // --- Lead pipeline funnel ---
  const stageCounts = OUTREACH_STAGES.map((stage) => ({
    ...stage,
    count: contacts.filter((c) => c.outreach_status === stage.id).length,
  }));
  const totalLeads = contacts.length;

  // --- Call metrics ---
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay()); // Sunday
  weekStart.setHours(0, 0, 0, 0);
  const prevWeekStart = new Date(weekStart);
  prevWeekStart.setDate(prevWeekStart.getDate() - 7);

  const thisWeekCalls = conversations.filter(
    (c) => new Date(c.created_at) >= weekStart,
  );
  const lastWeekCalls = conversations.filter((c) => {
    const d = new Date(c.created_at);
    return d >= prevWeekStart && d < weekStart;
  });

  const callsThisWeek = thisWeekCalls.length;
  const bookedThisWeek = thisWeekCalls.filter(
    (c) => c.appointment_booked,
  ).length;
  const callsLastWeek = lastWeekCalls.length;
  const bookedLastWeek = lastWeekCalls.filter(
    (c) => c.appointment_booked,
  ).length;

  const callTrend = callsLastWeek
    ? Math.round(((callsThisWeek - callsLastWeek) / callsLastWeek) * 100)
    : 0;
  const bookTrend = bookedLastWeek
    ? Math.round(((bookedThisWeek - bookedLastWeek) / bookedLastWeek) * 100)
    : 0;

  const allTimeBooked = conversations.filter(
    (c) => c.appointment_booked,
  ).length;
  const allTimeConversion = conversations.length
    ? Math.round((allTimeBooked / conversations.length) * 100)
    : 0;

  const durations = conversations.filter(
    (c) => c.duration_seconds && c.duration_seconds > 0,
  );
  const avgSeconds = durations.length
    ? Math.round(
        durations.reduce((s, c) => s + (c.duration_seconds || 0), 0) /
          durations.length,
      )
    : 0;
  const avgHandle = avgSeconds
    ? `${Math.floor(avgSeconds / 60)}m ${avgSeconds % 60}s`
    : "—";

  // --- 14-day call chart ---
  const callChart = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    const dateStr = d.toISOString().split("T")[0];
    const label = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const dayCalls = conversations.filter(
      (c) => c.created_at.split("T")[0] === dateStr,
    );
    return {
      date: label,
      calls: dayCalls.length,
      booked: dayCalls.filter((c) => c.appointment_booked).length,
    };
  });

  // --- Recent calls ---
  const recentCalls = conversations.slice(0, 6);

  if (loading) {
    return (
      <div className="bg-page-background min-h-screen flex items-center justify-center">
        <div
          className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#3ECF8E", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <div className="bg-page-background min-h-screen">
      {/* Page header */}
      <div className="bg-white border-b border-[#E8ECF0] px-8 pt-7 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-[0.08em] mb-1">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </p>
            <h1 className="text-[20px] font-bold text-[#0F1629] tracking-tight">
              {getGreeting()}, {displayName}
            </h1>
          </div>
          <div
            className="flex items-center gap-2.5 rounded-full px-3.5 py-2 mt-1 border"
            style={{
              background: "rgba(62,207,142,0.07)",
              borderColor: "rgba(62,207,142,0.18)",
            }}
          >
            <div className="relative w-1.5 h-1.5 shrink-0">
              <div
                className="absolute inset-0 rounded-full animate-ping opacity-60"
                style={{ background: "#3ECF8E" }}
              />
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: "#3ECF8E" }}
              />
            </div>
            <span
              className="text-[12px] font-semibold"
              style={{ color: "#1A8C5B" }}
            >
              Sera is live · {SERA_PHONE}
            </span>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">
        {/* KPI row */}
        <div className="grid grid-cols-4 gap-4">
          <KPICard
            label="Calls This Week"
            value={callsThisWeek}
            sub={`vs ${callsLastWeek} last week`}
            icon={Phone}
            trend={callTrend}
          />
          <KPICard
            label="Booked This Week"
            value={bookedThisWeek}
            sub={`vs ${bookedLastWeek} last week`}
            icon={Calendar}
            trend={bookTrend}
            accent
          />
          <KPICard
            label="All-Time Conversion"
            value={`${allTimeConversion}%`}
            sub="industry avg: 38%"
            icon={TrendingUp}
          />
          <KPICard
            label="Avg Handle Time"
            value={avgHandle}
            sub="fully automated"
            icon={Clock}
          />
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-12 gap-5">
          {/* Left 8 cols */}
          <div className="col-span-8 space-y-5">
            {/* 14-day call trend */}
            <div className="bg-white rounded-2xl border border-[#E8ECF0] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-[14px] font-semibold text-[#0F1629]">
                    Call Volume
                  </h2>
                  <p className="text-[12px] text-slate-400 mt-0.5">
                    Last 14 days
                  </p>
                </div>
                <div className="flex items-center gap-4 text-[11px] text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-sm inline-block"
                      style={{ background: "#3ECF8E" }}
                    />
                    Booked
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-sm bg-slate-200 inline-block" />
                    Info
                  </span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={callChart} barSize={18} barGap={2}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#F1F5F9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    interval={1}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    width={20}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "#F8FAFC" }}
                  />
                  <Bar
                    dataKey="booked"
                    stackId="a"
                    fill="#3ECF8E"
                    name="Booked"
                  />
                  <Bar
                    dataKey="calls"
                    stackId="a"
                    fill="#E2E8F0"
                    radius={[3, 3, 0, 0]}
                    name="Total"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Lead Pipeline Funnel */}
            <div className="bg-white rounded-2xl border border-[#E8ECF0] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-[14px] font-semibold text-[#0F1629]">
                    Outreach Pipeline
                  </h2>
                  <p className="text-[12px] text-slate-400 mt-0.5">
                    {totalLeads.toLocaleString()} total leads
                  </p>
                </div>
                <a
                  href="/contacts"
                  className="flex items-center gap-1 text-[12px] text-slate-400 hover:text-slate-600 no-underline transition-colors"
                >
                  View leads <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>
              <div className="space-y-2.5">
                {stageCounts
                  .filter((s) => s.id !== "not_contacted" || s.count > 0)
                  .map((stage) => {
                    const pct = totalLeads
                      ? Math.round((stage.count / totalLeads) * 100)
                      : 0;
                    return (
                      <div key={stage.id} className="flex items-center gap-3">
                        <p className="w-28 text-[12px] text-slate-500 shrink-0 truncate">
                          {stage.label}
                        </p>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${pct}%`,
                              background: stage.color,
                            }}
                          />
                        </div>
                        <p className="w-10 text-[12px] font-bold text-[#0F1629] tabular-nums text-right shrink-0">
                          {stage.count.toLocaleString()}
                        </p>
                      </div>
                    );
                  })}
              </div>
              {totalLeads === 0 && (
                <div className="py-6 text-center">
                  <Target className="w-6 h-6 text-slate-200 mx-auto mb-2" />
                  <p className="text-[12px] text-slate-400">
                    Import leads from the Contacts page to see your pipeline.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right 4 cols */}
          <div className="col-span-4 space-y-4">
            {/* Pipeline summary stats */}
            <div className="bg-white rounded-2xl border border-[#E8ECF0] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
              <h2 className="text-[13px] font-semibold text-[#0F1629] mb-4">
                Pipeline Snapshot
              </h2>
              <div className="space-y-3">
                {[
                  {
                    label: "Leads in pipeline",
                    value: totalLeads.toLocaleString(),
                    icon: Users,
                  },
                  {
                    label: "Replied / Engaged",
                    value: contacts
                      .filter((c) =>
                        [
                          "replied",
                          "call_scheduled",
                          "demo_done",
                          "proposal_sent",
                          "negotiating",
                          "closed_won",
                        ].includes(c.outreach_status || ""),
                      )
                      .length.toLocaleString(),
                    icon: TrendingUp,
                  },
                  {
                    label: "Closed (Won)",
                    value: contacts
                      .filter((c) => c.outreach_status === "closed_won")
                      .length.toLocaleString(),
                    icon: Calendar,
                  },
                  {
                    label: "All-time calls",
                    value: conversations.length.toLocaleString(),
                    icon: Phone,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2.5">
                      <item.icon className="w-3.5 h-3.5 text-slate-300" />
                      <span className="text-[12px] text-slate-500">
                        {item.label}
                      </span>
                    </div>
                    <span className="text-[13px] font-bold text-[#0F1629] tabular-nums">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent activity */}
            <div className="bg-white rounded-2xl border border-[#E8ECF0] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[13px] font-semibold text-[#0F1629]">
                  Recent Calls
                </h2>
                <a
                  href="/conversations"
                  className="text-slate-400 hover:text-slate-600 no-underline transition-colors"
                >
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
              {recentCalls.length === 0 ? (
                <div className="py-4 text-center">
                  <Phone className="w-5 h-5 text-slate-200 mx-auto mb-2" />
                  <p className="text-[12px] text-slate-400">No calls yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentCalls.map((c) => {
                    const name = c.caller_name || "Unknown";
                    const initials = name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();
                    const timeAgo = (() => {
                      const diff =
                        Date.now() - new Date(c.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 60) return `${mins}m ago`;
                      const hrs = Math.floor(mins / 60);
                      if (hrs < 24) return `${hrs}h ago`;
                      return `${Math.floor(hrs / 24)}d ago`;
                    })();
                    return (
                      <div key={c.id} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-bold text-[#0F1629] shrink-0">
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-semibold text-[#0F1629] truncate">
                            {name}
                          </p>
                          <p className="text-[11px] text-slate-400">
                            {timeAgo}
                          </p>
                        </div>
                        <div
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{
                            background: c.appointment_booked
                              ? "#3ECF8E"
                              : "#CBD5E1",
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
